"use client";

import { useEffect } from "react";
import { useNotificationsStore } from "@/lib/store/notifications-store";
import { useAuthStore } from "@/lib/store/auth-store";
import { notificationsApi } from "@/lib/notifications";

const POLL_MS = 20000;

// Montado en el root layout (app/layout.tsx) — la campanita es global
// (dashboard, admin y explorador público). Gateado por sesión, a diferencia
// de InterestCountsPoller (endpoint público).
//
// El dropdown de la campanita nunca debe mostrar un loading al abrirse: acá
// se hace un fetch silencioso de las últimas notificaciones apenas monta
// (sesión activa) y, después, solo cuando latestAt avanza respecto al último
// valor conocido — así llega una notificación nueva sin que el usuario
// tenga que abrir la campanita para "pedirla".
//
// El "último valor conocido" vive en el store (applyUnreadCount devuelve si
// cambió), no en un useRef local acá — otros flujos (ej. marcar leída una
// notificación desde su detalle) también actualizan el conteo/latestAt, y
// si el poller comparara contra su propia copia quedaría desincronizado y
// podría no detectar una notificación nueva que llegó justo después.
export function NotificationsPoller() {
  const isLoggedIn = useAuthStore((s) => s.user !== null);

  useEffect(() => {
    if (!isLoggedIn) return;

    // El primer poll no debe disparar un refreshItems propio — el mount ya
    // hace su propio fetch explícito de items más abajo (evita duplicarlo si
    // latestAt de entrada no es null).
    let isFirstPoll = true;

    function refreshItems() {
      notificationsApi
        .list(1, 10)
        .then((page) => useNotificationsStore.getState().setItems(page.data))
        .catch(() => {});
    }

    function poll() {
      if (document.visibilityState !== "visible") return;
      notificationsApi
        .unreadCount()
        .then(({ count, latestAt }) => {
          const changed = useNotificationsStore.getState().applyUnreadCount({ count, latestAt });
          if (changed && !isFirstPoll) refreshItems();
          isFirstPoll = false;
        })
        .catch(() => {});
    }

    poll();
    refreshItems();
    const interval = setInterval(poll, POLL_MS);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  return null;
}
